import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapPin, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { resolvePlaceToProperty } from '@/lib/callables'
import { openPropertyProfileOnMap } from '@/lib/property-navigation'
import { cn } from '@/lib/utils'

type Suggestion = {
  placeId: string
  primary: string
  secondary: string
}

type PlacesLib = NonNullable<ReturnType<typeof useMapsLibrary<'places'>>>
type SessionToken = InstanceType<PlacesLib['AutocompleteSessionToken']>
type PlacePrediction = NonNullable<
  Parameters<
    NonNullable<Parameters<PlacesLib['AutocompleteService']['prototype']['getPlacePredictions']>[1]>
  >[0]
>[number]
type PlaceResult = NonNullable<
  Parameters<NonNullable<Parameters<PlacesLib['PlacesService']['prototype']['getDetails']>[1]>>[0]
>

/**
 * Places Autocomplete → resolvePlaceToProperty for Biddle inventory search.
 */
export function PlacesPropertySearchBox({
  autoFocus,
  className,
  placeholder = 'Search any Australian address…',
}: {
  autoFocus?: boolean
  className?: string
  placeholder?: string
}) {
  const navigate = useNavigate()
  const places = useMapsLibrary('places')
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const sessionTokenRef = useRef<SessionToken | null>(null)
  const debounceRef = useRef<number | null>(null)
  const selectedLabelRef = useRef<string | null>(null)
  const requestIdRef = useRef(0)

  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [error, setError] = useState<string | null>(null)
  const [notInBiddle, setNotInBiddle] = useState(false)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!places) return
    sessionTokenRef.current = new places.AutocompleteSessionToken()
  }, [places])

  useEffect(() => {
    if (!places) return
    if (selectedLabelRef.current != null && query === selectedLabelRef.current) {
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      return
    }

    const q = query.trim()
    if (q.length < 2) {
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      setNotInBiddle(false)
      return
    }

    if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
    const requestId = ++requestIdRef.current
    setLoading(true)
    setNotInBiddle(false)
    setError(null)
    debounceRef.current = window.setTimeout(() => {
      const service = new places.AutocompleteService()
      service.getPlacePredictions(
        {
          input: q,
          componentRestrictions: { country: 'au' },
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (predictions: PlacePrediction[] | null, status: string) => {
          if (requestId !== requestIdRef.current) return
          setLoading(false)
          if (status !== places.PlacesServiceStatus.OK || !predictions) {
            setSuggestions([])
            setOpen(false)
            return
          }
          setSuggestions(
            predictions.map((p: PlacePrediction) => ({
              placeId: p.place_id,
              primary: p.structured_formatting?.main_text || p.description,
              secondary: p.structured_formatting?.secondary_text || '',
            })),
          )
          setOpen(true)
          setActiveIndex(-1)
        },
      )
    }, 200)

    return () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
    }
  }, [places, query])

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const root = rootRef.current
      if (!root) return
      if (e.target instanceof Node && !root.contains(e.target)) {
        setFocused(false)
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  async function selectPlace(placeId: string) {
    if (!places || resolving) return
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    requestIdRef.current += 1
    setResolving(true)
    setOpen(false)
    setSuggestions([])
    setError(null)
    setNotInBiddle(false)

    const attribution = document.createElement('div')
    const service = new places.PlacesService(attribution)
    service.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'name'],
        sessionToken: sessionTokenRef.current ?? undefined,
      },
      async (place: PlaceResult | null, status: string) => {
        sessionTokenRef.current = new places.AutocompleteSessionToken()
        if (status !== places.PlacesServiceStatus.OK || !place) {
          setResolving(false)
          setError('Could not look up that address. Try again.')
          return
        }

        const formatted =
          place.formatted_address || place.name || query.trim()
        selectedLabelRef.current = formatted
        setQuery(formatted)

        try {
          const result = await resolvePlaceToProperty(placeId, formatted)
          if (result.status === 'found' && result.property?.id) {
            setQuery('')
            selectedLabelRef.current = null
            openPropertyProfileOnMap(navigate, result.property.id)
          } else {
            setNotInBiddle(true)
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Search failed')
        } finally {
          setResolving(false)
        }
      },
    )
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      if (suggestions.length === 0) return
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.min(i < 0 ? 0 : i + 1, suggestions.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      if (suggestions.length === 0) return
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.max(i < 0 ? suggestions.length - 1 : i - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      if (suggestions.length === 0) return
      e.preventDefault()
      const index = activeIndex >= 0 ? activeIndex : 0
      void selectPlace(suggestions[index].placeId)
    }
  }

  const showSuggestions = focused && open && suggestions.length > 0

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <Input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => {
            selectedLabelRef.current = null
            setQuery(e.target.value)
            setNotInBiddle(false)
          }}
          onFocus={() => setFocused(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-12 pl-10 pr-10 text-base focus-visible:ring-1 focus-visible:ring-biddle-blue"
          aria-label="Search properties by address"
          aria-expanded={showSuggestions}
          aria-controls={listId}
        />
        {(loading || resolving) && focused ? (
          <Spinner className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        ) : null}
      </div>
      {error && focused ? <p className="mt-2 text-sm text-danger">{error}</p> : null}
      {notInBiddle && focused ? (
        <p className="mt-2 text-sm text-muted">Not in Biddle yet.</p>
      ) : null}
      {showSuggestions ? (
        <ul
          id={listId}
          className="absolute z-30 mt-2 max-h-80 w-full overflow-auto rounded-2xl border border-border bg-white py-1 shadow-xl"
        >
          {suggestions.map((item, index) => (
            <li key={item.placeId}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface',
                  index === activeIndex && 'bg-surface',
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void selectPlace(item.placeId)}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-navy">{item.primary}</span>
                  {item.secondary ? (
                    <span className="block text-xs text-muted">{item.secondary}</span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
